import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { ApiError } from '../lib/api';
import type { OnlineState, PolicyStatus, Reachability, TargetStatus } from '../lib/api';

export function Panel({ title, children, actions }: { title?: string; children: ReactNode; actions?: ReactNode }) {
  return (
    <section className="panel">
      {(title || actions) && (
        <div className="panel-head">
          {title ? <h3>{title}</h3> : <span />}
          {actions && <div className="actions">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

export function Notice({ kind, children }: { kind: 'error' | 'warning' | 'success' | 'info'; children: ReactNode }) {
  return <div className={`notice ${kind}`}>{children}</div>;
}

/** Renders an API failure with its server-written, admin-readable message. */
export function ErrorNotice({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  if (!error) return null;
  const message = error instanceof ApiError ? error.message : String(error);
  const details = error instanceof ApiError ? error.details : null;

  const issues = Array.isArray(details)
    ? (details as Array<{ field?: string; message: string }>)
    : Array.isArray((details as { issues?: unknown })?.issues)
      ? ((details as { issues: Array<{ field: string; message: string }> }).issues)
      : null;

  return (
    <Notice kind="error">
      <strong>{message}</strong>
      {issues && (
        <ul>
          {issues.map((issue, i) => (
            <li key={i}>{issue.field ? `${issue.field}: ` : ''}{issue.message}</li>
          ))}
        </ul>
      )}
      {(details as { remediation?: string })?.remediation && (
        <p style={{ margin: '8px 0 0' }}>{(details as { remediation: string }).remediation}</p>
      )}
      {onRetry && (
        <div className="actions" style={{ marginTop: 10 }}>
          <button type="button" className="small" onClick={onRetry}>
            Retry
          </button>
        </div>
      )}
    </Notice>
  );
}

export function OnlineBadge({ state }: { state: OnlineState }) {
  // `dot` adds a mark before the word, so the state does not rest on hue alone.
  if (state === 'online') return <span className="badge ok dot">Online</span>;
  if (state === 'offline') return <span className="badge danger dot">Offline</span>;
  return <span className="badge muted dot">Never seen</span>;
}

/**
 * The reason a tablet is not answering, not merely the fact that it is silent.
 *
 * The full evidence remains on the detail page; `title` makes the conclusion
 * available here without making every fleet table two lines taller.
 */
export function ReachabilityBadge({ reachability }: { reachability: Reachability }) {
  const tone =
    reachability.classification === 'ONLINE'
      ? 'ok'
      : reachability.classification === 'UNKNOWN'
        ? 'muted'
        : reachability.classification === 'POWERED_OFF'
          ? 'info'
          : reachability.severity === 'critical' || reachability.severity === 'high'
            ? 'danger'
            : 'warn';

  return (
    <span className={`badge ${tone} dot`} title={reachability.conclusion}>
      {reachability.headline}
    </span>
  );
}

/**
 * Policy status is shown as assigned-vs-applied, never collapsed into one
 * value: a device can be assigned a policy it has not applied because it has
 * been offline, and the admin needs to see that difference.
 */
export function PolicyStatusBadge({ status }: { status: PolicyStatus }) {
  switch (status) {
    case 'applied': return <span className="badge ok">APPLIED</span>;
    case 'pending': return <span className="badge warn">PENDING</span>;
    case 'outdated': return <span className="badge warn">OUT OF DATE</span>;
    case 'partial': return <span className="badge warn">PARTIAL</span>;
    case 'failed': return <span className="badge danger">NOT ENFORCED</span>;
    default: return <span className="badge muted">NONE</span>;
  }
}

export function DeploymentStatusBadge({ status }: { status: TargetStatus | string }) {
  const map: Record<string, string> = {
    installed: 'ok',
    completed: 'ok',
    failed: 'danger',
    cancelled: 'muted',
    awaiting_device: 'warn',
    pending: 'muted',
    notified: 'info',
    downloading: 'info',
    installing: 'info',
    in_progress: 'info',
    partial: 'warn',
  };
  return <span className={`badge ${map[status] ?? 'muted'}`}>{status.replace(/_/g, ' ').toUpperCase()}</span>;
}

export function ManagementBadge({ state }: { state: string }) {
  if (state === 'device_owner') return <span className="badge ok">DEVICE OWNER</span>;
  if (state === 'profile_owner') return <span className="badge warn">PROFILE OWNER</span>;
  if (state === 'unmanaged') return <span className="badge danger">UNMANAGED</span>;
  return <span className="badge muted">UNKNOWN</span>;
}

export function relativeTime(iso: string | null): string {
  if (!iso) return 'never';
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 0) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) { value /= 1024; unit += 1; }
  return `${value.toFixed(1)} ${units[unit]}`;
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

/**
 * Loading state.
 *
 * Skeleton rows rather than the word "Loading" wherever the shape of what is
 * coming is known: the region keeps its height, so the page does not jump when
 * the data lands, and the reader can already see how much is on the way.
 */
export function Loading({ what = 'data', rows = 3 }: { what?: string; rows?: number }) {
  return (
    <div className="loading-block" role="status" aria-live="polite">
      <span className="sr-only-text muted small">Loading {what}…</span>
      {Array.from({ length: rows }, (_, i) => (
        <div className="skeleton" key={i} />
      ))}
    </div>
  );
}

/**
 * Empty state.
 *
 * `title` is the fact ("No devices enrolled yet"); the children explain what to
 * do about it. A blank panel tells the reader nothing about whether the system
 * is broken or simply has nothing to show.
 */
export function Empty({ title, children }: { title?: string; children?: ReactNode }) {
  return (
    <div className="empty">
      {title && <div className="empty-title">{title}</div>}
      {children}
    </div>
  );
}

/**
 * A confirmation dialog for actions that are awkward to undo.
 *
 * Deliberately not a `window.confirm`: the browser dialog cannot show WHICH
 * student and WHICH device are involved, and naming them is the entire point of
 * asking. The confirm button carries the intent styling so the consequence is
 * legible before the click, and Escape and the backdrop both cancel — the safe
 * direction is always the easy one.
 */
export function ConfirmDialog({
  title,
  intro,
  children,
  confirmLabel,
  intent = 'primary',
  busy = false,
  onConfirm,
  onCancel,
}: {
  title: string;
  intro?: string;
  children?: ReactNode;
  confirmLabel: string;
  intent?: 'primary' | 'danger';
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="modal" role="dialog" aria-modal="true" aria-label={title}>
        <h3>{title}</h3>
        {intro && <p className="modal-sub">{intro}</p>}
        {children}
        <div className="modal-actions">
          <button type="button" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="button" className={intent} onClick={onConfirm} disabled={busy}>
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * A student, as the primary identifier of a device.
 *
 * `null` is an ordinary state, not an error: every device enrolled before
 * assignments existed has no student. It renders as "Unassigned" rather than
 * "Unknown", which would suggest the system had lost track of something.
 */
export function StudentCell({
  student,
}: {
  student: { studentName: string | null; studentRef: string | null } | null;
}) {
  if (!student?.studentName) {
    return (
      <div className="student-cell">
        <span className="unassigned">Unassigned</span>
      </div>
    );
  }
  return (
    <div className="student-cell">
      <span className="name">{student.studentName}</span>
      {student.studentRef && <span className="ref">{student.studentRef}</span>}
    </div>
  );
}

/**
 * Fetches on mount and optionally polls.
 *
 * Polling exists because the Custom DPC provider is poll-based end to end:
 * devices check in on their own schedule, so the dashboard cannot be pushed
 * updates either. Refreshing every few seconds is how an admin watches a
 * deployment progress.
 */
export function useAsync<T>(
  fn: (signal: AbortSignal) => Promise<T>,
  deps: unknown[],
  pollMs?: number,
): { data: T | null; error: unknown; loading: boolean; reload: () => void } {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    const run = async (isPoll: boolean) => {
      if (!isPoll) setLoading(true);
      try {
        const result = await fn(controller.signal);
        if (!cancelled) { setData(result); setError(null); }
      } catch (err) {
        if (!cancelled && (err as Error).name !== 'AbortError') setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void run(false);
    const timer = pollMs ? setInterval(() => void run(true), pollMs) : null;

    return () => {
      cancelled = true;
      controller.abort();
      if (timer) clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce, pollMs]);

  return { data, error, loading, reload: () => setNonce((n) => n + 1) };
}
